import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Tooltip,
  IconButton,
  CircularProgress,
  Pagination,
  Snackbar,
  Alert,
  Stack,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Drawer,
  Grid,
  Divider,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import { getPickList, getHoldList, getPickHistory, markItemAsPicked, markItemAsUnpicked, markItemAsHold, getLocations, findVariantByExactSku } from '../../services/productapi';
import apiClient from '../../services/api';

// Helper function to get complete image URL
const getFullImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // If it already starts with http or https, it's already a full URL
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Otherwise, prepend the API base URL
  // Remove leading slash if present to prevent double slashes
  const cleanPath = imagePath.startsWith('/') ? imagePath.substr(1) : imagePath;
  return `${apiClient.defaults.baseURL}/${cleanPath}`;
};

const PickListPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pickList, setPickList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState('pending');
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [totalItems, setTotalItems] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [variantDetails, setVariantDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch pick list data
  const fetchPickList = async () => {
    setLoading(true);
    try {
      const params = { page };
      if (selectedLocation) {
        params.location = selectedLocation;
      }
      let response;
      if (activeTab === 'pending') {
        response = await getPickList(params);
      } else if (activeTab === 'hold') {
        response = await getHoldList(params);
      } else {
        response = await getPickHistory(params);
      }
      setPickList(response.data.results);
      const count = response.data.count || response.data.results?.length || 0;
      setTotalPages(Math.max(1, Math.ceil(count / 50)));
      setTotalItems(response.data.total_items || 0);
    } catch (error) {
      console.error('Error fetching pick list:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load pick list items',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Mark item as picked
  const markAsPicked = async (id) => {
    try {
      await markItemAsPicked(id);
      // Remove the picked item from the list
      setPickList(prev => prev.filter(item => item.id !== id));
      setSnackbar({
        open: true,
        message: 'Item marked as picked successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error marking item as picked:', error);
      setSnackbar({
        open: true,
        message: 'Failed to mark item as picked',
        severity: 'error'
      });
    }
  };

  // Mark item as unpicked (undo)
  const markAsUnpicked = async (id) => {
    try {
      await markItemAsUnpicked(id);
      // Remove the unpicked item from history list
      setPickList(prev => prev.filter(item => item.id !== id));
      setSnackbar({
        open: true,
        message: 'Item moved back to pick list',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error marking item as unpicked:', error);
      setSnackbar({
        open: true,
        message: 'Failed to move item back to pick list',
        severity: 'error'
      });
    }
  };

  const markAsHold = async (id) => {
    try {
      await markItemAsHold(id);
      setPickList(prev => prev.filter(item => item.id !== id));
      setSnackbar({
        open: true,
        message: 'Item moved to hold list',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error putting item on hold:', error);
      setSnackbar({
        open: true,
        message: 'Failed to move item to hold list',
        severity: 'error'
      });
    }
  };

  // Load locations on initial render
  useEffect(() => {
    fetchLocations();
  }, []);

  // Load data on initial render and when page or location changes
  useEffect(() => {
    fetchPickList();
  }, [page, selectedLocation, activeTab]);

  // Fetch all locations
  const fetchLocations = async () => {
    try {
      const response = await getLocations();
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load locations',
        severity: 'error'
      });
    }
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle location change
  const handleLocationChange = (event) => {
    setSelectedLocation(event.target.value);
    setPage(1); // Reset to first page when location changes
  };

  const handleTabChange = (event, value) => {
    setActiveTab(value);
    setPage(1);
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };
  
  // Handle opening the detail drawer
  const handleOpenDrawer = async (item) => {
    setSelectedItem(item);
    setDrawerOpen(true);
    setLoadingDetails(true);
    
    try {
      // Fetch detailed variant information using SKU
      const response = await findVariantByExactSku(item.variant_sku);
      setVariantDetails(response.data);
      console.log("Variant details:", response.data);
    } catch (error) {
      console.error("Error fetching variant details:", error);
      setSnackbar({
        open: true,
        message: 'Failed to load product details',
        severity: 'error'
      });
    } finally {
      setLoadingDetails(false);
    }
  };
  
  // Handle closing the detail drawer
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setVariantDetails(null);
  };

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs items={[{ label: 'Dashboard', path: '/dashboard' }, { label: 'Pick List', path: '/pick-list' }]} />
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Pick List</Typography>
        <Stack direction="row" spacing={2}>
          <Button 
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={fetchPickList}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
          <Tab value="pending" label="To Pick" />
          <Tab value="hold" label="Hold List" />
          <Tab value="history" label="Picked History" />
        </Tabs>
      </Paper>
      
      {/* Location Filter */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="h2" gutterBottom>
            {activeTab === 'pending'
              ? 'Filter by Location'
              : activeTab === 'hold'
              ? 'Hold by Location'
              : 'History by Location'}
          </Typography>
          {totalItems > 0 && (
            <Chip label={<span>Items: <strong>{totalItems}</strong></span>} color="primary" />
          )}
        </Box>
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel id="location-select-label">Select Location</InputLabel>
          <Select
            labelId="location-select-label"
            value={selectedLocation}
            onChange={handleLocationChange}
            label="Select Location"
          >
            <MenuItem value="">All Locations</MenuItem>
            {locations.map((location) => (
              <MenuItem key={location.id} value={location.name}>
                {location.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>
      
      {/* Pick List Table */}
      <Paper elevation={2} sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: isMobile ? 96 : 'auto' }}>
                  {isMobile ? 'Item' : 'SKU'}
                </TableCell>
                <TableCell>Details</TableCell>
                <TableCell align="center">Image</TableCell>
                {!isMobile && <TableCell align="center">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 3 : 4} align="center">
                    <CircularProgress size={30} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : pickList.length > 0 ? (
                pickList.map((item) => (
                  <TableRow 
                    key={item.id}
                    hover
                    onClick={() => handleOpenDrawer(item)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {item.variant_sku}
                      </Typography>
                      {isMobile && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          {activeTab === 'pending' ? (
                            <>
                              <Tooltip title="Mark as Picked">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsPicked(item.id);
                                  }}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Put on Hold">
                                <IconButton
                                  color="warning"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsHold(item.id);
                                  }}
                                >
                                  <PauseCircleOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : activeTab === 'hold' ? (
                            <Tooltip title="Mark as Picked">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsPicked(item.id);
                                }}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Move back to Pick List">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsUnpicked(item.id);
                                }}
                              >
                                <UndoIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight="bold" noWrap={isMobile}>
                          {item.product_name}
                        </Typography>
                        <Typography variant="body2">{item.marketplace}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.location_name}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                        <Chip
                          size={isMobile ? 'small' : 'medium'}
                          label={`Stock: ${item.variant_stock}`}
                          color={
                            item.variant_stock > 0
                              ? "success" 
                              : item.variant_stock === 0
                              ? "primary" 
                              : "error" 
                          }
                          sx={{ fontWeight: 'bold' }}
                        />
                        <Chip
                          size={isMobile ? 'small' : 'medium'}
                          label={(
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon fontSize="small" />
                              <span>{item.variant_stock_packed ?? 0}</span>
                            </Box>
                          )}
                          color={(item.variant_stock_packed ?? 0) > 0 ? "info" : "default"}
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      {item.variant_image ? (
                        <Box
                          component="img"
                          src={getFullImageUrl(item.variant_image)}
                          alt={item.product_name}
                          sx={{ width: 60, height: 84, objectFit: 'contain' }}
                        />
                      ) : (
                        <Typography variant="caption">No Image</Typography>
                      )}
                    </TableCell>
                    {!isMobile && (
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        {activeTab === 'pending' ? (
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="Mark as Picked">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsPicked(item.id);
                                }}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Put on Hold">
                              <IconButton
                                color="warning"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsHold(item.id);
                                }}
                              >
                                <PauseCircleOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : activeTab === 'hold' ? (
                          <Tooltip title="Mark as Picked">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsPicked(item.id);
                              }}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Move back to Pick List">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsUnpicked(item.id);
                              }}
                            >
                              <UndoIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {activeTab === 'pending'
                      ? 'No items to pick'
                      : activeTab === 'hold'
                      ? 'No items in hold list'
                      : 'No picked history'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pickList.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={handlePageChange} 
              color="primary" 
            />
          </Box>
        )}
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Product Detail Drawer - Simplified */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            maxHeight: '70vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            p: 2
          }
        }}
      >
        {selectedItem && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Product Details</Typography>
              <IconButton onClick={handleCloseDrawer} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            {loadingDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                {/* Product image */}
                <Box sx={{ mb: 3, width: { xs: '100%', sm: '50%', md: '40%' }, maxWidth: 300, textAlign: 'center' }}>
                  {(variantDetails?.image || selectedItem.variant_image) ? (
                    <Box 
                      component="img"
                      src={getFullImageUrl(variantDetails?.image || selectedItem.variant_image)}
                      alt={variantDetails?.product_name || selectedItem.product_name}
                      sx={{ 
                        width: '100%',
                        maxHeight: 280,
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        height: 100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#f5f5f5',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">No Image</Typography>
                    </Box>
                  )}
                </Box>
                
                {/* Status indicators */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Chip 
                    label={`Stock: ${variantDetails?.stock ?? selectedItem.variant_stock}`} 
                    color={(variantDetails?.stock ?? selectedItem.variant_stock) <= 0 ? "error" : "success"}
                    sx={{ fontWeight: 'bold' }}
                  />
                  <Chip 
                    label={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EmailIcon fontSize="small" />
                        <span>{variantDetails?.stock_packed ?? selectedItem.variant_stock_packed ?? 0}</span>
                      </Box>
                    )}
                    color={(variantDetails?.stock_packed ?? selectedItem.variant_stock_packed ?? 0) > 0 ? "info" : "default"}
                    sx={{ fontWeight: 'bold' }}
                  />
                  {variantDetails?.state && (
                    <>
                      {/* <Chip 
                        label={`Status: ${variantDetails.state}`}
                        color={variantDetails.state === 'active' ? 'info' : 'default'}
                      /> */}
                      <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`http://pujoy.in/product/${selectedItem.variant_sku}`, '_blank');
                        }}
                        sx={{ minHeight: 32 }}
                      >
                        View Online
                      </Button>
                    </>
                  )}
                </Box>
                
                {/* Product details table */}
                <TableContainer sx={{ width: '100%', maxWidth: 700, mb: 3 }}>
                  <Table size="small" sx={{ width: '100%' }}>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          Product
                        </TableCell>
                        <TableCell>{variantDetails?.product_name || selectedItem.product_name}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          SKU
                        </TableCell>
                        <TableCell>{selectedItem.variant_sku}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          Category
                        </TableCell>
                        <TableCell>{variantDetails?.category_name || selectedItem.variant_category}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          Material
                        </TableCell>
                        <TableCell>{variantDetails?.material || "Not specified"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          Location
                        </TableCell>
                        <TableCell>{variantDetails?.location_name || selectedItem.location_name || "Not assigned"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          Price Info
                        </TableCell>
                        <TableCell>
                          {variantDetails?.mrp && <>MRP: ₹{variantDetails.mrp}<br /></>}
                          {variantDetails?.cost_price && <>Cost: ₹{variantDetails.cost_price}</>}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          Sales Statistics
                        </TableCell>
                        <TableCell>
                          {variantDetails?.sale !== undefined && <>Sales: {variantDetails.sale} units<br /></>}
                          {variantDetails?.return_quantity !== undefined && <>Returns: {variantDetails.return_quantity} units</>}
                        </TableCell>
                      </TableRow>
                      {(variantDetails?.color_name || selectedItem.color_name) && (
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                            Color
                          </TableCell>
                          <TableCell>{variantDetails?.color_name || selectedItem.color_name}</TableCell>
                        </TableRow>
                      )}
                      {(variantDetails?.size_name || selectedItem.size_name) && (
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                            Size
                          </TableCell>
                          <TableCell>{variantDetails?.size_name || selectedItem.size_name}</TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          Marketplace
                        </TableCell>
                        <TableCell>{selectedItem.marketplace}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          Quantity
                        </TableCell>
                        <TableCell>{Math.abs(selectedItem.quantity)} units</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Action button */}
                {/* <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => {
                    markAsPicked(selectedItem.id);
                    handleCloseDrawer();
                  }}
                  sx={{ minWidth: 200 }}
                >
                  Mark as Picked
                </Button> */}
              </Box>
            )}
          </>
        )}
      </Drawer>
    </Box>
  );
};

export default PickListPage;
